import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { useIsEmailHubNewsletterPhaseAEnabled } from "@circle-react/hooks/useIsEmailHubNewsletterPhaseAEnabled";
import { useIsEmailHubNewsletterPhaseEEnabled } from "@circle-react/hooks/useIsEmailHubNewsletterPhaseEEnabled";
import { newsletterRoutes } from "@circle-react/lib/routes/newsletter";

import { t } from "@/i18n-js/instance";
import { useNewsletterActivation } from "@/react/components/SettingsApp/Newsletters/hooks/useNewsletterActivation";

import { NavGroup } from "../NavGroup";
import { SubNavLinkItem } from "./SubNavLinkItem";
import type { Link } from "./types";

export const useNewsletterLinks = (): Link[] => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);
  const isNewsletterEnabled = useIsEmailHubNewsletterPhaseAEnabled();
  const isOverviewEnabled = useIsEmailHubNewsletterPhaseEEnabled();
  const { isActivationRequired } = useNewsletterActivation();
  const isDisabled = !isAdmin || !isNewsletterEnabled;

  if (isActivationRequired) return [];

  const overviewLinks: Link[] = isOverviewEnabled
    ? [
        {
          label: t("common.overview"),
          path: newsletterRoutes.overview(),
          exact: true,
          isDisabled,
        },
      ]
    : [];

  return [
    ...overviewLinks,
    {
      label: t("common.posts"),
      path: newsletterRoutes.posts(),
      // Non-exact so nested editor routes keep Posts (and the group) active.
      exact: false,
      isDisabled,
    },
    {
      label: t("common.lists"),
      path: newsletterRoutes.lists(),
      exact: true,
      isDisabled,
    },
  ];
};

export const NewsletterLinks = () => {
  const { isAvailable } = useNewsletterActivation();
  const links = useNewsletterLinks();

  if (!isAvailable || links.length === 0) {
    return null;
  }

  return (
    <NavGroup groupLinks={links}>
      <NavGroup.Content title={t("settings_nav_v3.newsletter.title")}>
        {links.map(link => (
          <SubNavLinkItem
            key={link.path}
            label={link.label}
            to={link.path}
            exact={link.exact}
            isDisabled={link.isDisabled}
          />
        ))}
      </NavGroup.Content>
    </NavGroup>
  );
};
